import { Box, IconButton, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import CopyToast from './gradient-fade-box/CopyToast';
import { useCopyOnDoubleClick } from './gradient-fade-box/useCopyOnDoubleClick';

type Props = {
  children: React.ReactNode;
  /** Background color to fade to — pass the card/container background, e.g. theme.palette.background.paper */
  fadeColor: string;
  /** Width/height of each gradient edge in px. Default: 60 */
  fadeSize?: number;
  overflowX?: 'auto' | 'scroll' | 'hidden';
  overflowY?: 'auto' | 'scroll' | 'hidden';
  /** Whether to show the right edge gradient */
  showRight?: boolean;
  /** Whether to show the left edge gradient */
  showLeft?: boolean;
  /** Whether to show the bottom edge gradient */
  showBottom?: boolean;
  sx?: object;
};

/**
 * A scroll container that fades content into the background color at the edges
 * instead of showing hard cutoffs. Gradients appear/disappear based on actual
 * scroll position — no gradient shown if there is nothing more to scroll.
 *
 * When horizontal overflow exists a toggle button appears: pan mode (grab-drag
 * to scroll) or select mode (normal cursor, text selection works).
 */
const GradientFadeBox = ({
  children,
  fadeColor,
  fadeSize = 60,
  overflowX = 'auto',
  overflowY = 'hidden',
  showRight = true,
  showLeft = true,
  showBottom = true,
  sx
}: Props) => {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [panMode, setPanMode] = useState(true);
  const [isBottomClipped, setIsBottomClipped] = useState(false);
  const dragStart = useRef<{ x: number; scrollLeft: number } | null>(null);
  const { toast, handleDoubleClick, handleTouchEnd } = useCopyOnDoubleClick();

  const canPan = canScrollLeft || canScrollRight;
  const panActive = panMode && canPan;

  const updateState = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollDown(el.scrollTop < el.scrollHeight - el.clientHeight - 2);
    }
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setIsBottomClipped(rect.bottom > window.innerHeight);
    }
  }, []);

  const scheduleUpdate = useCallback(() => {
    requestAnimationFrame(updateState);
  }, [updateState]);

  useEffect(() => {
    scheduleUpdate();

    const ro = new ResizeObserver(scheduleUpdate);
    const el = scrollRef.current;

    // Observe children too — content growth (rows, font/image load, column width settle)
    // bumps scrollWidth without changing scrollRef's own size, so RO on parent alone misses it.
    const observeChildren = () => {
      if (!el) return;
      Array.from(el.children).forEach((child) => ro.observe(child));
    };

    const mo = new MutationObserver(() => {
      observeChildren();
      scheduleUpdate();
    });

    if (el) {
      ro.observe(el);
      observeChildren();
      mo.observe(el, { childList: true, subtree: true, attributes: false, characterData: false });
    }

    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [scheduleUpdate]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!panActive || !scrollRef.current) return;
      dragStart.current = { x: e.clientX, scrollLeft: scrollRef.current.scrollLeft };
      setIsDragging(true);
      e.preventDefault();
    },
    [panActive]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !dragStart.current || !scrollRef.current) return;
      const delta = dragStart.current.x - e.clientX;
      scrollRef.current.scrollLeft = dragStart.current.scrollLeft + delta;
    },
    [isDragging]
  );

  const stopDrag = useCallback(() => {
    setIsDragging(false);
    dragStart.current = null;
  }, []);

  const cursor = isDragging ? 'grabbing' : panActive ? 'grab' : 'default';

  return (
    <Box
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ position: 'relative', display: 'flex', flexDirection: 'column', ...sx }}
    >
      {/* Scrollable content */}
      <Box
        ref={scrollRef}
        onScroll={updateState}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        onDoubleClick={handleDoubleClick}
        onTouchEnd={handleTouchEnd}
        onDragStart={(e) => e.preventDefault()}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX,
          overflowY,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          cursor,
          touchAction: 'manipulation',
          userSelect: isDragging ? 'none' : 'auto'
        }}
      >
        {children}
      </Box>

      {/* Pan/Select mode toggle — visible on container hover, only when horizontal overflow exists */}
      {canPan && (
        <Tooltip title={panMode ? 'Switch to select mode' : 'Switch to pan mode'} placement="left">
          <IconButton
            size="small"
            onClick={() => setPanMode((prev) => !prev)}
            sx={{
              position: isBottomClipped ? 'fixed' : 'absolute',
              bottom: isBottomClipped ? 16 : 2,
              right: isBottomClipped ? 16 : 4,
              zIndex: isBottomClipped ? 1300 : 2,
              width: 24,
              height: 24,
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              color: 'text.secondary',
              borderRadius: '4px',
              border: 'none',
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.2s ease, background-color 0.15s ease',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.1)'
              }
            }}
          >
            {panMode ? (
              /* Open hand / pan */
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 3.5A1.5 1.5 0 0 1 10.5 2h.01A1.5 1.5 0 0 1 12 3.5V9a.5.5 0 0 0 1 0V5.5A1.5 1.5 0 0 1 14.5 4h.01A1.5 1.5 0 0 1 16 5.5V9a.5.5 0 0 0 1 0V7.5A1.5 1.5 0 0 1 18.5 6h.01A1.5 1.5 0 0 1 20 7.5v5A7 7 0 0 1 6 12V5.5A1.5 1.5 0 0 1 7.5 4h.01A1.5 1.5 0 0 1 9 5.5V9a.5.5 0 0 0 1 0V3.5z" />
              </svg>
            ) : (
              /* Arrow cursor / select */
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 3l14 8-6.5 2L10 21 5 3z" />
                <path d="M10 13l2 7 1.5-4.5L18 14l-8-1z" fillOpacity="0" />
              </svg>
            )}
          </IconButton>
        </Tooltip>
      )}

      {/* Right edge fade */}
      {showRight && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: `${fadeSize}px`,
            background: `linear-gradient(to right, transparent, ${fadeColor})`,
            pointerEvents: 'none',
            opacity: canScrollRight ? 1 : 0,
            transition: 'opacity 0.25s ease'
          }}
        />
      )}

      {/* Left edge fade */}
      {showLeft && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${fadeSize}px`,
            background: `linear-gradient(to left, transparent, ${fadeColor})`,
            pointerEvents: 'none',
            opacity: canScrollLeft ? 1 : 0,
            transition: 'opacity 0.25s ease'
          }}
        />
      )}

      {/* Bottom edge fade */}
      {showBottom && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: `${fadeSize}px`,
            background: `linear-gradient(to bottom, transparent, ${fadeColor})`,
            pointerEvents: 'none',
            opacity: canScrollDown ? 1 : 0,
            transition: 'opacity 0.25s ease'
          }}
        />
      )}

      <CopyToast toast={toast} />
    </Box>
  );
};

export default GradientFadeBox;
